/**
 * Landing page copy.
 *
 * Anything wrapped in [square brackets] is a PLACEHOLDER. The page renders
 * bracketed text with a yellow highlight so it can't ship unnoticed.
 * Replace it with genuine content (or delete the item) before launch.
 * Search this file for "[" to find every placeholder.
 */

export const ctaLabel = "Buy program";
export const ctaLabelShort = "Buy program";
export const sprintCtaLabel = "Time your 20m sprint test";
export const openProgramLabel = "Open program";

export const price = "$60 USD";

export const hero = {
  eyebrow: "10-week sprint program · AFL · Rugby League · Football",
  title: "Sprint train like an elite track athlete.",
  subtitle:
    "Improve acceleration, running efficiency and essential sprint mechanics that enhance your athleticism on the field.",
  trustLine: "Written by Olympian Tori West",
  // Set `src` to a sprint clip (mp4) or photo to replace the placeholder panel.
  media: {
    type: "video" as "video" | "image",
    src: "",
    poster: "",
    alt: "Athlete accelerating out of a start",
  },
  mediaCallout: {
    label: "",
    value:
      "Improve your sprint mechanics and run with better efficiency and explosive power.",
  },
};

export const proofStats: { value: string; label: string }[] = [];

export const whyFirst20 = {
  title: "What this program improves",
  intro:
    "All three of these components are key fundamentals that elite track athletes use to sprint, and they can be applied to every running-based ball sport. They improve overall athleticism, and critically, speed.",
};

export const benefits = [
  {
    title: "Acceleration",
    body: "How fast you start, from zero to 20 m.",
    test: "20 m sprint",
  },
  {
    title: "Max velocity",
    body: "The maximum speed at which you can sprint.",
    test: "30 m fly sprint",
  },
  {
    title: "Speed endurance",
    body: "How long you can maintain max velocity, for more lactic-based work.",
    test: "300 m sprint",
  },
];

export const programFacts = [
  { label: "Length", value: "10 weeks of training, plus a test in week 11" },
  { label: "Sessions", value: "3 per week" },
  { label: "Session length", value: "60–90 min" },
  { label: "Equipment", value: "Athletics track, cones, small wickets, stopwatch or phone" },
  { label: "Level", value: "Ages 14+" },
  { label: "Delivered", value: "Week by week in PEAK on your phone" },
];

export const phases = [
  {
    weeks: "Weeks 1–4",
    title: "Foundations",
    body: "Learn basic sprint drills, acceleration positions, and top-end sprint positions, and understand the importance of giving each rep intent. You need to learn basic sprint mechanics before layering on more load.",
  },
  {
    weeks: "Weeks 5–7",
    title: "Speed endurance and top-end mechanics",
    body: "Start layering on speed-endurance load, applying your new sprint mechanics to build fitness. Work on improving top-end speed and accelerations.",
  },
  {
    weeks: "Weeks 8–10",
    title: "Piece everything together",
    body: "Running load decreases and speed intensity increases. Sprint sessions now sit on a higher base, in preparation for the test in week 11. By the end of this program you should be peaking for speed and ready to hit your season.",
  },
];

export const sampleWeekNote =
  "All sessions include sprint drill videos & demonstrations.";

export const sampleWeek = [
  {
    day: "Day 1",
    title: "Accelerations & Plyometrics",
    duration: "",
    items: [
      "2 × 20 m bounds, rudimentary hops, Bulgarian plyos",
      "2 × 15 m and 2 × 30 m sprints, superset with hill sprints or sled pulls",
      "3 × 30 m wicket runs",
    ],
  },
  {
    day: "Day 2",
    title: "Speed Endurance",
    duration: "",
    items: ["2 × 30 m wicket runs", "6 × 150 m, 6 min rest, at 90%"],
  },
  {
    day: "Day 3",
    title: "Top End",
    duration: "",
    items: [
      "3 × 30 m wicket runs",
      "3 × 30 m flys",
      "2 × 60 m, 6 min rest, and 1 × 80 m at 100%",
    ],
  },
];

export const sports = [
  {
    title: "AFL",
    body: "Break away from a stoppage, lead into space, and win more ground-ball contests.",
  },
  {
    title: "Rugby League",
    body: "Hit the line harder, chase kicks faster, and get back onside sooner.",
  },
  {
    title: "Football",
    body: "Beat your marker to the ball and run in behind before the defence reacts.",
  },
];

export const inSeasonNote =
  "If you are in season and already do fitness running, remove the speed endurance session and just do the top-end sprint mechanics and accelerations and plyometrics sessions. Two times per week is enough to see benefits.";

export const coach = {
  name: "Tori West",
  role: "Heptathlete · 2024 Paris Olympics",
  photo: "/tori-west.jpg",
  bio: "I do the Olympic Hybrid Athletic Event. Speed and running mechanics are the basis of the sport I do.",
  credentials: [
    "Commonwealth Games bronze medallist",
    "Olympian, 2024 Paris Olympics",
    "2-time Australian champion",
  ],
};

export const testimonials: {
  quote: string;
  name: string;
  detail: string;
  result: string;
}[] = [];

export const steps = [
  {
    title: "Create your account",
    body: `Sign up and pay ${price} once. No subscription.`,
  },
  {
    title: "Run the three tests",
    body: "In weeks 1, 4 and 11 you do a 20 m sprint, a 30 m fly, and a 300 m. Acceleration, max velocity, and speed endurance.",
  },
  {
    title: "Train week by week",
    body: "Open each session in PEAK, follow the drills, and test again in weeks 4 and 11.",
  },
];

export const offer = {
  title: "10-week sprint program",
  subtitle: "One payment. Full program.",
  includes: [
    "10 weeks of training, laid out week by week",
    "Sprint drill videos and demonstrations in every session",
    "Tests in weeks 1, 4 and 11: 20 m sprint, 30 m fly, and 300 m",
    "Scheduling guidance for in-season and off-season",
  ],
};

export const faqItems = [
  {
    question: "Which sports is this for?",
    answer:
      "AFL, Rugby League, and football. The program trains acceleration, which matters in all three.",
  },
  {
    question: "What does it train?",
    answer:
      "Acceleration, max velocity, and speed endurance. You test all three with a 20 m sprint, a 30 m fly, and a 300 m.",
  },
  {
    question: "Do I stop my normal training?",
    answer:
      "No. Keep training with your club. The program is designed to run alongside it.",
  },
  {
    question: "How much time does it take each week?",
    answer: "2–3 sessions of about 60–90 minutes.",
  },
  {
    question: "What equipment do I need?",
    answer:
      "An athletics track, cones, running wickets, and a phone or stopwatch to time your sprints.",
  },
  {
    question: "What age or level is it for?",
    answer: "Athletes aged 14 and over, from junior to senior level.",
  },
  {
    question: "Can I do it in season?",
    answer: inSeasonNote,
  },
  {
    question: "How much does it cost?",
    answer: `${price}, paid once. It's charged in US dollars, and your bank converts it at checkout.`,
  },
  {
    question: "How long do I have access?",
    answer:
      "You have lifetime access. The program is saved in your account and you can easily restart it if you wish.",
  },
];

export const finalCta = {
  title: "Sprint train like an elite track athlete.",
  body: `10 weeks of training, with tests in weeks 1, 4 and 11. ${price}, paid once. Start this week.`,
};

export const meta = {
  title: "PEAK | Sprint train like an elite track athlete",
  description:
    "Improve acceleration, running efficiency and essential sprint mechanics that enhance your athleticism on the field.",
};

/** Handwritten (Caveat) notes scattered around the page for a human touch. */
export const handNotes = {
  hero: "where games are won",
  heroCallout: "your new PB?",
  benefits: "quality > quantity",
  program: "a peek inside",
  coach: "Tori West",
  offer: "one-off. no subscription!",
  finalCta: "see you at the start line",
};

export const tickerItems = [
  "AFL",
  "Rugby League",
  "Football",
  "10 weeks",
  "First 20 m",
  "Written by an Olympian",
  "Sprint mechanics",
];
